/**
 * Service IA unifié — routage intelligent entre providers
 * Local d'abord (Ollama), LiteLLM en intermédiaire, Claude en fallback
 */

export type AIProvider = "local" | "claude" | "litellm" | "auto" | "disabled";

export type TaskComplexity = "simple" | "medium" | "complex";

export interface AIConfig {
  provider: AIProvider;
  ollama_url: string;
  ollama_model: string;
  litellm_url: string;
  litellm_key: string;
  anthropic_key: string;
  claude_model: string;
  temperature: number;
  max_tokens: number;
}

export interface AIRequest {
  prompt: string;
  system?: string;
  context?: Record<string, unknown>;
  stream?: boolean;
  provider?: AIProvider;
  complexity?: TaskComplexity;
}

export interface AIResponse {
  content: string;
  provider_used: string;
  model_used: string;
  tokens_used?: number;
  duration_ms: number;
}

export interface Anomaly {
  type: "price_high" | "price_low" | "low_margin" | "missing_cost" | "duplicate" | "missing_item";
  severity: "info" | "warning" | "critical";
  line_id?: string;
  message: string;
  suggestion?: string;
}

export type AIActionType =
  | { type: "navigate"; path: string }
  | { type: "create_devis"; data: Record<string, unknown> }
  | { type: "search_product"; query: string }
  | { type: "search_client"; query: string }
  | { type: "generate_pdf"; doc_type: string; doc_id: number }
  | { type: "show_data"; data: unknown; label?: string }
  | { type: "none"; message: string };

export const DEFAULT_AI_CONFIG: AIConfig = {
  provider: "auto",
  ollama_url: "http://100.64.0.4:11434",
  ollama_model: "qwen2.5:32b",
  litellm_url: "http://100.64.0.14:4000",
  litellm_key: "sk-sovreai-local",
  anthropic_key: "",
  claude_model: "claude-sonnet-4-20250514",
  temperature: 0.3,
  max_tokens: 4096,
};

const AI_CONFIG_KEY = "doliui-ai-config";

export function getAIConfig(): AIConfig {
  if (typeof window === "undefined") return DEFAULT_AI_CONFIG;
  try {
    const stored = localStorage.getItem(AI_CONFIG_KEY);
    if (stored) return { ...DEFAULT_AI_CONFIG, ...JSON.parse(stored) };
  } catch {
    // ignore
  }
  return DEFAULT_AI_CONFIG;
}

export function saveAIConfig(config: Partial<AIConfig>): AIConfig {
  const current = getAIConfig();
  const merged = { ...current, ...config };
  localStorage.setItem(AI_CONFIG_KEY, JSON.stringify(merged));
  return merged;
}

/**
 * Déterminer la complexité d'une tâche pour le routage
 */
export function estimateComplexity(prompt: string, system?: string): TaskComplexity {
  const len = prompt.length + (system?.length || 0);
  const complexKeywords = /analy|compar|raisonne|multi|stratég|optimis|rapport|résumé complet/i;
  const simpleKeywords = /format|extrai|tradui|complèt|suggest|list|cherch/i;

  if (len > 2000 || complexKeywords.test(prompt)) return "complex";
  if (len < 500 && simpleKeywords.test(prompt)) return "simple";
  return "medium";
}

/**
 * Choisir le provider selon la complexité et la config
 * Mode auto : simple→local, medium→litellm (fallback local), complex→claude (fallback litellm→local)
 */
export function selectProvider(config: AIConfig, complexity: TaskComplexity): string {
  if (config.provider === "disabled") return "disabled";
  if (config.provider === "local") return "local";
  if (config.provider === "claude") return "claude";
  if (config.provider === "litellm") return "litellm";

  // Mode auto — route by complexity
  switch (complexity) {
    case "simple":
      return "local";
    case "medium":
      return "litellm";
    case "complex":
      return "claude";
    default:
      return "local";
  }
}

/**
 * Chaîne de fallback pour le mode auto
 */
export function getFallbackChain(provider: string): string[] {
  switch (provider) {
    case "claude":
      return ["claude", "litellm", "local"];
    case "litellm":
      return ["litellm", "local"];
    case "local":
      return ["local"];
    default:
      return ["local"];
  }
}

/**
 * Contexte de page pour l'assistant
 */
export interface PageContext {
  page: string;
  path: string;
  data?: Record<string, unknown>;
  suggestions?: string[];
}

export function buildPageContext(pathname: string, data?: Record<string, unknown>): PageContext {
  if (pathname === "/" || pathname === "") {
    return {
      page: "Dashboard",
      path: pathname,
      data,
      suggestions: [
        "Résumer la situation du mois",
        "Quels clients relancer ?",
        "Devis en attente depuis plus de 30 jours",
        "Taux de conversion des devis",
      ],
    };
  }

  if (pathname.match(/^\/devis\/[^/]+$/)) {
    return {
      page: "Détail devis",
      path: pathname,
      data,
      suggestions: [
        "Analyser la marge de ce devis",
        "Comparer avec des devis similaires",
        "Vérifier les prix fournisseur",
        "Détecter les anomalies",
        "Suggérer des lignes manquantes",
      ],
    };
  }

  if (pathname === "/devis") {
    return {
      page: "Liste des devis",
      path: pathname,
      data,
      suggestions: [
        "Devis en attente depuis plus de 30 jours",
        "Taux de conversion ce mois",
        "Top devis par montant",
        "Devis signés non facturés",
      ],
    };
  }

  if (pathname.match(/^\/factures/)) {
    return {
      page: "Factures",
      path: pathname,
      data,
      suggestions: [
        "Factures impayées",
        "CA du mois en cours",
        "Factures en retard de paiement",
        "Top clients par CA",
      ],
    };
  }

  if (pathname.match(/^\/tiers\/[^/]+$/)) {
    return {
      page: "Détail client",
      path: pathname,
      data,
      suggestions: [
        "Résumer ce client",
        "Historique des commandes",
        "Encours client",
        "Derniers devis",
      ],
    };
  }

  if (pathname === "/tiers") {
    return {
      page: "Liste des tiers",
      path: pathname,
      data,
      suggestions: [
        "Clients sans activité depuis 6 mois",
        "Top clients par CA",
        "Prospects à relancer",
      ],
    };
  }

  if (pathname.match(/^\/produits/)) {
    return {
      page: "Produits",
      path: pathname,
      data,
      suggestions: [
        "Produits sans prix de vente",
        "Produits les plus vendus",
        "Chercher un équivalent",
      ],
    };
  }

  if (pathname.match(/^\/chantiers/)) {
    return {
      page: "Chantiers",
      path: pathname,
      data,
      suggestions: [
        "Chantiers en cours",
        "Rentabilité des chantiers",
        "Planning de la semaine",
      ],
    };
  }

  if (pathname.match(/^\/crm\/prospects/)) {
    return {
      page: "Pipeline prospects",
      path: pathname,
      data,
      suggestions: [
        "Prospects à relancer cette semaine",
        "Synthèse du pipeline par étape",
        "Comment qualifier un nouveau prospect ?",
      ],
    };
  }

  return {
    page: "Page",
    path: pathname,
    data,
    suggestions: [
      "Que puis-je faire pour vous ?",
      "Résumer la situation",
    ],
  };
}
